import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import FilterBar from '../ui/FilterBar';
import useUrlFilters from '../ui/useUrlFilters';
import DataTable, { ColumnsMenu, useColumnVisibility } from '../ui/DataTable';
import ListPage, { useListQuery } from '../ui/ListPage';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { formatCount, formatDateTime, plural } from '../ui/format';
import { imagePath } from '../ui/entityRoutes';
import ExportButton from './ExportPanel';
import ImageTicketsPanel from './ImageTicketsPanel';
import { IMAGE_SEVERITY_OPTIONS, osLabel, shortDigest } from './imageFields';

const muted = (text) => <span className="text-graphite">{text}</span>;
const dash = (value) => value || muted('—');
const countCell = (n) => (n ? formatCount(n) : muted('0'));
const severityCount = (n, className) => (n ? <span className={`font-semibold ${className}`}>{formatCount(n)}</span> : muted('0'));

const FILTER_DEFAULTS = { kw: '', severity: '', scanned: '' };
const FILTERS = [
  { key: 'kw', type: 'search', label: 'Container image', placeholder: 'Name or path', mono: true, width: 260 },
  { key: 'severity', type: 'select', label: 'Has severity', options: IMAGE_SEVERITY_OPTIONS },
  {
    key: 'scanned',
    type: 'select',
    label: 'Last scanned',
    options: [
      { value: '', label: 'Any time' },
      { value: '7d', label: 'Last 7 days' },
      { value: '30d', label: 'Last 30 days' },
    ],
  },
];

function OsCell({ image }) {
  const os = osLabel(image.os_family, image.os_name);
  if (!os) return muted('—');
  return (
    <span className="inline-flex items-center gap-1.5">
      {os}
      {image.os_eosl && <Badge tone="med" title="The OS release no longer gets security updates">End of life</Badge>}
    </span>
  );
}

const COLUMNS = [
  {
    key: 'name',
    header: 'Container image',
    hideable: false,
    render: (i) => <EntityLink kind="image" id={i.id} name={i.name} title={i.image_key} />,
  },
  { key: 'repository', header: 'Repository', mono: true, render: (i) => dash(i.repository) },
  {
    key: 'digest',
    header: 'Current digest',
    mono: true,
    render: (i) => (i.current_digest ? <span title={i.current_digest}>{shortDigest(i.current_digest)}</span> : muted('Unknown')),
  },
  { key: 'tags', header: 'Tags', mono: true, maxWidth: 200, fullText: (i) => (i.current_tags || []).join(' '), render: (i) => dash((i.current_tags || []).join(' ')) },
  { key: 'os', header: 'OS', render: (i) => <OsCell image={i} /> },
  { key: 'arch', header: 'Architecture', defaultHidden: true, render: (i) => dash(i.architecture) },
  { key: 'findings', header: 'Findings', align: 'right', render: (i) => countCell(i.finding_count) },
  { key: 'critical', header: 'Critical', align: 'right', render: (i) => severityCount(i.critical_count, 'text-crit') },
  { key: 'high', header: 'High', align: 'right', render: (i) => severityCount(i.high_count, 'text-high') },
  { key: 'medium', header: 'Medium', align: 'right', render: (i) => severityCount(i.medium_count, 'text-med') },
  { key: 'low', header: 'Low', align: 'right', render: (i) => countCell(i.low_count) },
  { key: 'vulns', header: 'Vulnerabilities', align: 'right', defaultHidden: true, render: (i) => countCell(i.vuln_count) },
  { key: 'scanned', header: 'Last scanned', render: (i) => <span className="text-graphite tabular-nums">{formatDateTime(i.last_scanned_at) || '—'}</span> },
];

export default function ContainerImages() {
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);
  const columnState = useColumnVisibility(COLUMNS, 'sbom-columns-container-images');
  const [selected, setSelected] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);

  const params = { kw: filters.kw, severity: filters.severity || undefined, scanned_within: filters.scanned || undefined };
  const key = JSON.stringify(params);
  const fetchPage = useCallback((paging) => API.GET_IMAGES({ ...params, ...paging }), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = useListQuery(fetchPage, key, 'images');

  useEffect(() => setSelected([]), [key]);

  // Selection is kept across pages, so remember each selected image's name for the panel.
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!query.rows?.length) return;
    setNames((prev) => ({ ...prev, ...Object.fromEntries(query.rows.map((i) => [i.id, i.name])) }));
  }, [query.rows]);
  const selectedImages = selected.map((id) => ({ id, name: names[id] || `Container image ${id}` }));

  const rowMenu = (i) => [
    { key: 'findings', label: 'View findings', to: imagePath(i, 'findings') },
    { key: 'scans', label: 'View scans', to: imagePath(i, 'scans') },
  ];

  const filtered = Object.keys(FILTER_DEFAULTS).some((k) => filters[k]);
  const empty = filtered ? (
    <EmptyState title="No container images match these filters" action={<Button onClick={() => setFilters(FILTER_DEFAULTS)}>Clear filters</Button>}>
      The container image filter matches part of the image path. Widen the severity and last scanned filters, or clear them.
    </EmptyState>
  ) : (
    <EmptyState title="No container images found">
      Container images appear here once the image scanner uploads its reports and the exporter ingests them.
    </EmptyState>
  );

  return (
    <ListPage>
      <PageHeader
        title="Container images"
        actions={(
          <>
            <ColumnsMenu columns={COLUMNS} hidden={columnState.hidden} onToggle={columnState.toggle} onReset={columnState.reset} />
            <ExportButton />
          </>
        )}
      />
      <FilterBar className="mb-3" filters={FILTERS} values={filters} onChange={setFilters} />
      <DataTable
        className="flex-1"
        label="Container images"
        columns={COLUMNS}
        hidden={columnState.hidden}
        rows={query.rows}
        rowKey={(i) => i.id}
        rowLabel={(i) => i.name}
        rowMenu={rowMenu}
        selection={{ selected, onChange: setSelected }}
        selectionBar={(
          <>
            <span className="font-semibold tabular-nums">{plural(selected.length, 'container image')} selected</span>
            <span className="mx-1 h-4 border-l border-rule" aria-hidden="true" />
            <Button size="sm" variant="primary" onClick={() => setPanelOpen(true)}>
              <Plus size={14} strokeWidth={2} aria-hidden="true" />
              Create tickets
            </Button>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelected([])}>Clear selection</Button>
          </>
        )}
        loading={query.loading}
        error={query.error}
        errorTitle="Container images could not be loaded"
        onRetry={query.retry}
        empty={empty}
        pagination={query.pagination}
      />
      <ImageTicketsPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        images={selectedImages}
        onCreated={({ created }) => {
          if (created > 0) setSelected([]);
        }}
      />
    </ListPage>
  );
}
